import { EventEmitter, Injectable, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { IFilter, IPopulate, IQueryParam } from 'modelos/src';
import { Estados, IAlerta } from 'modelosSeguridad/src';
import { Howl } from 'howler';
import { IdleService } from './idle.service';
import { Lista2Service } from './lista2.service';
import { HelperService } from './helper.service';
import { WebSocketService } from './websocket.service';
import { ReconciliacionWs } from './ws-reconciliacion';
import { LoginService } from '../../modulos/login/login.service';

type SonidoAlerta = 'Sirena' | 'Campana' | 'Silencio';

/**
 *
 * Es el mismo criterio del badge de seguridad-monitoreo-web
 * (`alertasNuevas.service.ts`, tabla "Alertas nuevas"), replicado tal cual
 * para que los dos productos muestren el mismo número.
 *
 * Si se toca esta lista hay que tocar la de monitoreo, y al reves.
 */
const ESTADOS_SIN_ATENDER: Estados[] = [
  'Nueva',
  'Sin Tratamiento',
  'Liberada',
  'Derivada',
  'Derivada Finalizada',
  'Derivada Compartida Finalizada',
  'Derivada Externa Finalizada',
  'Devuelta',
];

const FILTRO_ALERTAS_NUEVAS: IFilter<IAlerta> = {
  activa: true,
  $and: [
    { $or: ESTADOS_SIN_ATENDER.map((estado) => ({ estadoActual: estado })) },
  ],
};

@Injectable({
  providedIn: 'root',
})
export class ListadoAlertasSeguridadVivoService {
  private lista2Service = inject(Lista2Service);
  private idleService = inject(IdleService);
  private helperService = inject(HelperService);
  private wsService = inject(WebSocketService);

  // 'alertas' es una pseudo-entidad que el server resuelve a rooms por
  // cliente de Seguridad. Hasta que el server la soporte, el subscribe se
  // rechaza (ack false) y este listado sigue con su polling de 30s.
  private reconciliacionWs = new ReconciliacionWs(this.wsService, {
    entidades: ['alertas'],
    idPermiso: () => LoginService.getIdPermiso(),
    relistar: () => this.listar(),
  });

  private alertasAnteriores: IAlerta[] = [];
  public alertas: IAlerta[] = [];
  public totalCount = 0;

  private sonidos = {
    Fondo: new Howl({
      src: ['/assets/sonidos/empty_loop_for_js_performance.ogg'],
      volume: 0.5,
      autoplay: false,
      loop: true,
    }),
    Sirena: new Howl({
      src: ['/assets/sonidos/sirena1.mp3'],
      volume: 1,
      autoplay: false,
      loop: false,
    }),
    Campana: new Howl({
      src: ['/assets/sonidos/campana.mp3'],
      volume: 1,
      autoplay: false,
      loop: false,
    }),
  };

  private alertas$?: Subscription;
  private idle$?: Subscription;
  public alertasChange = new EventEmitter<IAlerta[]>();

  private async listar(): Promise<void> {
    if (!this.puedeListar()) {
      this.totalCount = 0;
      return;
    }

    const populate: IPopulate[] = [{ path: 'boton', select: 'notificacion' }];

    const queryParams: IQueryParam = {
      filter: JSON.stringify(FILTRO_ALERTAS_NUEVAS),
      select: '_id idBoton estadoActual activa fechaCreacion',
      populate: JSON.stringify(populate),
      excludeTotalCount: true,
    };

    this.alertas$?.unsubscribe();
    this.alertas$ = this.lista2Service
      .alertasSeguridad(queryParams, this.reconciliacionWs.intervaloActual())
      .subscribe((data) => {
        this.totalCount = data.datos.length;
        this.alertasAnteriores = JSON.parse(JSON.stringify(this.alertas));
        this.alertas = JSON.parse(JSON.stringify(data.datos));
        this.alertasChange.emit(this.alertas);
        this.reproducirSonido();
      });
  }

  private hayNuevaAlerta(): IAlerta | undefined {
    return this.alertas.find(
      (a) => !this.alertasAnteriores.map((ant) => ant._id).includes(a._id),
    );
  }

  private reproducirSonido() {
    if (this.helperService.sonidoMuteado) return;
    const alertaNueva = this.hayNuevaAlerta();
    if (alertaNueva) {
      const sonido = alertaNueva.boton?.notificacion as
        | SonidoAlerta
        | undefined;
      switch (sonido) {
        case 'Silencio':
          break;
        case 'Sirena':
          this.sonidos.Sirena.play();
          break;
        case 'Campana':
          this.sonidos.Campana.play();
          break;
        default:
          this.sonidos.Campana.play();
          break;
      }
    }
  }

  private InitBackSound() {
    if (this.helperService.sonidoMuteado) return;
    this.sonidos.Fondo.play();
  }

  private stopBackSound() {
    this.sonidos.Fondo.stop();
  }

  /**
   * Sin credenciales de Seguridad el backend corta antes de pegarle a la API
   * de Seguridad y devuelve un listado vacio: no tiene sentido pollear.
   */
  private puedeListar(): boolean {
    if (!this.helperService.tieneModulo('Alertas de Seguridad')) return false;
    return !!LoginService.getPermisoActual()?.tieneCredencialesSeguridad;
  }

  public async iniciarServicio(): Promise<void> {
    if (!this.puedeListar()) return;

    // Una sola vez por sesión: NavigationComponent llama iniciarServicio() en
    // cada creación (ej: logout → login) y los handlers se acumulaban.
    if (!this.idle$) {
      this.idle$ = new Subscription();
      this.idle$.add(
        this.idleService.idle$.subscribe(() => this.InitBackSound()),
      );
      this.idle$.add(
        this.idleService.wake$.subscribe(() => this.stopBackSound()),
      );
    }
    this.reconciliacionWs.iniciar();
    await this.listar();
  }

  public async reiniciar(): Promise<void> {
    this.alertas$?.unsubscribe();
    this.alertas = [];
    this.alertasAnteriores = [];
    this.totalCount = 0;
    if (!this.puedeListar()) {
      this.reconciliacionWs.detener();
      return;
    }
    this.reconciliacionWs.iniciar();
    await this.listar();
  }
}
